import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  RefreshCw, Play, Square, AlertCircle, CheckCircle2,
  ArrowLeft, Loader2, Wrench, ListChecks,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const Ross1000BackfillMonitor = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [daysBack, setDaysBack] = useState(90);
  // Incomplete bookings
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [incompleteBookings, setIncompleteBookings] = useState(null);
  const [incompleteDays, setIncompleteDays] = useState(90);
  // Single backfill dialog
  const [singleBookingResult, setSingleBookingResult] = useState(null);
  const [singleLoadingId, setSingleLoadingId] = useState(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('hw_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/ross1000/backfill-status`, {
        headers: authHeaders(),
      });
      setStatus(r.data);
    } catch (e) {
      console.error('fetch status error', e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchStatus();
    if (autoRefresh) {
      const t = setInterval(fetchStatus, 5000);
      return () => clearInterval(t);
    }
  }, [fetchStatus, autoRefresh]);

  const handleRestart = async () => {
    if (!confirm(`Riavviare il backfill su ${daysBack} giorni di storico?`)) return;
    setRestarting(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/ross1000/backfill-start?days_back=${daysBack}`,
        {},
        { headers: authHeaders() }
      );
      if (r.data.success) {
        toast.success(r.data.message);
        fetchStatus();
      } else {
        toast.error(r.data.message);
      }
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRestarting(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Vuoi cancellare il backfill in corso?')) return;
    setCancelling(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/ross1000/backfill-cancel`,
        {},
        { headers: authHeaders() }
      );
      toast(r.data.message);
      fetchStatus();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCancelling(false);
    }
  };

  const fetchIncomplete = async () => {
    setIncompleteLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/ross1000/incomplete-bookings?days_back=${incompleteDays}`,
        { headers: authHeaders() }
      );
      setIncompleteBookings(r.data);
      if (r.data.count === 0) {
        toast.success('Nessun booking con anomalie!');
      } else {
        toast.info(`Trovati ${r.data.count} booking con anomalie`);
      }
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIncompleteLoading(false);
    }
  };

  const handleBackfillSingle = async (bookingId) => {
    setSingleLoadingId(bookingId);
    setSingleBookingResult(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/ross1000/backfill-booking/${bookingId}`,
        {},
        { headers: authHeaders() }
      );
      setSingleBookingResult(r.data);
      if (r.data.success) {
        toast.success(`Backfill eseguito per ${r.data.guest_name}`);
        fetchIncomplete(); // refresh
      }
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
      setSingleBookingResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setSingleLoadingId(null);
    }
  };

  const getStatusBadge = (s) => {
    const colors = {
      ready: 'bg-slate-100 text-slate-800',
      starting: 'bg-blue-100 text-blue-800',
      running: 'bg-amber-100 text-amber-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-orange-100 text-orange-800',
      not_initialized: 'bg-gray-100 text-gray-800',
    };
    return colors[s] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/arrivi-partenze">
          <Button variant="ghost" size="sm" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
          </Button>
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="page-title">
          <Wrench className="h-6 w-6 text-blue-600" />
          ROSS1000 Backfill Monitor
        </h1>
      </div>

      {/* Status overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Stato del Job</span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" size="sm"
                onClick={() => setAutoRefresh((v) => !v)}
                data-testid="toggle-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </Button>
              <Button variant="ghost" size="sm" onClick={fetchStatus} data-testid="manual-refresh-btn">
                Aggiorna
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Label className="font-semibold">Status:</Label>
              <Badge className={getStatusBadge(status?.status)} data-testid="job-status-badge">
                {status?.status || 'unknown'}
              </Badge>
              {status?.is_running_now && (
                <Badge className="bg-amber-100 text-amber-800">
                  <Loader2 className="h-3 w-3 animate-spin mr-1 inline" /> In esecuzione
                </Badge>
              )}
            </div>

            {/* Progress bar */}
            {(status?.status === 'running' || status?.status === 'completed') && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Property processate</span>
                  <span data-testid="progress-pct">
                    {status?.properties_processed || 0} / {status?.total_properties || 0}
                    {' '}({status?.progress_pct || 0}%)
                  </span>
                </div>
                <Progress value={status?.progress_pct || 0} className="h-3" />
              </div>
            )}

            {status?.current_step && (
              <div className="text-sm text-slate-600">
                <strong>Step corrente:</strong> {status.current_step}
              </div>
            )}

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <StatBox label="Booking scansionati" value={status?.total_bookings_scanned} />
              <StatBox label="Famiglie incomplete" value={status?.bookings_with_missing_family} />
              <StatBox label="Partenze mancanti" value={status?.bookings_with_missing_departure} />
              <StatBox label="Doc da aggiornare" value={status?.bookings_with_missing_doc_fields} />
              <StatBox label="✅ Correzioni OK" value={status?.corrections_sent} color="green" />
              <StatBox label="❌ Correzioni fallite" value={status?.corrections_failed} color="red" />
              <StatBox label="Started at" value={fmtDate(status?.started_at)} small />
              <StatBox label="Completed at" value={fmtDate(status?.completed_at)} small />
            </div>

            {/* Errors */}
            {status?.errors && status.errors.length > 0 && (
              <details className="mt-3" data-testid="errors-list">
                <summary className="cursor-pointer text-sm font-semibold text-red-700">
                  ❌ {status.errors.length} errori registrati (clicca per dettagli)
                </summary>
                <div className="mt-2 max-h-64 overflow-auto bg-red-50 p-3 rounded text-xs">
                  {status.errors.map((e, i) => (
                    <div key={i} className="mb-2 pb-2 border-b border-red-200">
                      <div><strong>{e.property_name || e.property_id}</strong></div>
                      <div className="text-red-700">{e.error}</div>
                      <div className="text-slate-500">{e.at}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-3 border-t">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Storico (gg):</Label>
                <Input
                  type="number" value={daysBack}
                  onChange={(e) => setDaysBack(parseInt(e.target.value) || 90)}
                  className="w-20"
                  min={7} max={365}
                  data-testid="days-back-input"
                />
              </div>
              <Button
                onClick={handleRestart}
                disabled={restarting || status?.is_running_now}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="restart-btn"
              >
                {restarting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                Avvia/Riavvia
              </Button>
              <Button
                onClick={handleCancel}
                disabled={cancelling || !status?.is_running_now}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                data-testid="cancel-btn"
              >
                {cancelling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Square className="h-4 w-4 mr-1" />}
                Cancella job
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista booking incompleti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-amber-600" />
              Booking con anomalie identificate
            </span>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Giorni:</Label>
              <Input
                type="number" value={incompleteDays}
                onChange={(e) => setIncompleteDays(parseInt(e.target.value) || 90)}
                className="w-20"
                min={1} max={365}
                data-testid="incomplete-days-input"
              />
              <Button onClick={fetchIncomplete} disabled={incompleteLoading} data-testid="fetch-incomplete-btn">
                {incompleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Scansiona ora'}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {incompleteBookings === null && (
            <div className="text-center text-slate-500 py-6 text-sm">
              Clicca su "Scansiona ora" per identificare i booking ROSS1000 con anomalie residue.
              <br />Da qui puoi forzare la correzione singola.
            </div>
          )}
          {incompleteBookings && incompleteBookings.count === 0 && (
            <div className="flex items-center gap-2 text-green-700 py-4">
              <CheckCircle2 className="h-5 w-5" /> Nessuna anomalia trovata negli ultimi {incompleteDays} giorni!
            </div>
          )}
          {incompleteBookings && incompleteBookings.count > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-slate-600 mb-2">
                Trovati <strong>{incompleteBookings.count}</strong> booking con anomalie:
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left">
                      <th className="p-2">Property</th>
                      <th className="p-2">Ospite</th>
                      <th className="p-2">CI</th>
                      <th className="p-2">CO</th>
                      <th className="p-2">Doc</th>
                      <th className="p-2">Inviati</th>
                      <th className="p-2">Problemi</th>
                      <th className="p-2">Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incompleteBookings.items.map((b) => (
                      <tr key={b.booking_id} className="border-b" data-testid={`incomplete-row-${b.booking_id}`}>
                        <td className="p-2">{b.property_name}</td>
                        <td className="p-2 font-medium">{b.guest_name}</td>
                        <td className="p-2">{b.check_in}</td>
                        <td className="p-2">{b.check_out}</td>
                        <td className="p-2 text-center">{b.n_guest_documents}</td>
                        <td className="p-2 text-center">{b.n_sent_idswhs}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {b.problems.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-red-300 text-red-700">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-2">
                          <Button
                            size="sm"
                            onClick={() => handleBackfillSingle(b.booking_id)}
                            disabled={singleLoadingId === b.booking_id}
                            data-testid={`backfill-single-${b.booking_id}`}
                          >
                            {singleLoadingId === b.booking_id ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Invio...</>
                            ) : (
                              <><Wrench className="h-3 w-3 mr-1" /> Correggi</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog risultato singolo */}
      <Dialog open={!!singleBookingResult} onOpenChange={(o) => { if (!o) setSingleBookingResult(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Risultato Backfill Singolo</DialogTitle>
          </DialogHeader>
          {singleBookingResult && (
            <div className="space-y-3 text-sm">
              {singleBookingResult.success ? (
                <div className="flex items-center gap-2 text-green-700 font-semibold">
                  <CheckCircle2 className="h-5 w-5" /> Backfill eseguito per {singleBookingResult.guest_name}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700 font-semibold">
                  <AlertCircle className="h-5 w-5" /> Errore
                </div>
              )}
              {singleBookingResult.actions && (
                <div className="bg-slate-50 p-3 rounded">
                  <div className="font-semibold mb-2">Azioni eseguite:</div>
                  {singleBookingResult.actions.length === 0 ? (
                    <div className="text-slate-500">Nessuna azione necessaria</div>
                  ) : (
                    <ul className="space-y-1">
                      {singleBookingResult.actions.map((a, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {a.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertCircle className="h-4 w-4 text-red-600" />}
                          <span>{a.step}{a.n ? ` (${a.n} record)` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {singleBookingResult.error && (
                <div className="bg-red-50 p-3 rounded text-red-700">{singleBookingResult.error}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function StatBox({ label, value, color = 'slate', small = false }) {
  const colorMap = {
    slate: 'text-slate-900',
    green: 'text-green-700',
    red: 'text-red-700',
  };
  return (
    <div className="bg-slate-50 p-3 rounded">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`${small ? 'text-xs' : 'text-2xl font-bold'} ${colorMap[color] || colorMap.slate}`}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT');
  } catch {
    return iso;
  }
}

export default Ross1000BackfillMonitor;
